/**
 * Tokens visuais derivados do domínio forense para AnalisePage: // ux-neutrality:allow doc-comment
 *  - lrBadgeClasses: cor do badge da classificação verbal ENFSI (h1 → success,
 *    h2 → danger, inconclusivo → warning).
 *  - CRITERION_STATUS_STYLE: borda + ícone + badge por status do critério
 *    (convergente/divergente/inconclusiva/neutra).
 *
 * Mantido como módulo separado para preservar o limite de 400 linhas do
 * arquivo principal e isolar a tabela de cores do markup.
 */
import { AlertCircle, CheckCircle2, HelpCircle, XCircle, type LucideIcon } from 'lucide-react';
import type { CriterionStatus, LRClassification } from '@/lib/analyses';

/**
 * E54.F8.T08: a classificação verbal é RESULTADO — é o lugar onde a rampa de
 * sinal do pacote existe para ser usada. O que mudou foi a forma: retângulo
 * reto do `Badge`, sem pílula.
 */
export function lrBadgeClasses(classification: LRClassification): string {
  if (classification.startsWith('h1_')) {
    return 'border-success bg-success-50 text-success-700';
  }
  if (classification.startsWith('h2_')) {
    return 'border-danger bg-danger-50 text-danger-700';
  }
  return 'border-warning bg-warning-50 text-warning-700';
}

export interface CriterionStatusStyle {
  borderLeft: string;
  iconClass: string;
  badgeClass: string;
  Icon: LucideIcon;
}

// Tailwind precisa de class names completos no source — não interpolamos.
export const CRITERION_STATUS_STYLE: Record<CriterionStatus, CriterionStatusStyle> = {
  convergent: {
    borderLeft: 'border-l-success',
    iconClass: 'text-success-700',
    badgeClass: 'border-success bg-success-50 text-success-700',
    Icon: CheckCircle2,
  },
  divergent: {
    borderLeft: 'border-l-danger',
    iconClass: 'text-danger-700',
    badgeClass: 'border-danger bg-danger-50 text-danger-700',
    Icon: XCircle,
  },
  inconclusive: {
    borderLeft: 'border-l-warning',
    iconClass: 'text-warning-700',
    badgeClass: 'border-warning bg-warning-50 text-warning-700',
    Icon: AlertCircle,
  },
  neutral: {
    borderLeft: 'border-l-border',
    iconClass: 'text-fg-dim',
    badgeClass: 'border-border bg-surface-2 text-fg-muted',
    Icon: HelpCircle,
  },
};
